import {
    Box,
    Checkbox,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { DraggableSyntheticListeners } from '@dnd-kit/core';
import { GripVertical, X } from 'lucide-react';
import ExcludeBooleanIcon from '~/components/Icons/ExcludeBooleanIcon';
import ExtractBooleanIcon from '~/components/Icons/ExtractBooleanIcon';
import IntersectionBooleanIcon from '~/components/Icons/IntersectionBooleanIcon';
import UniteBooleanIcon from '~/components/Icons/UniteBooleanIcon';
import { BooleanOperation } from '~/engine/util/algorithms/sdf/SDFOperations';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

// Menu props for Select components
export const menuProps = {
    disablePortal: false,
    disableScrollLock: true,
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    slotProps: { paper: { sx: { maxHeight: 200, zIndex: 1500 } } },
};

// Shared components
export const Label = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body2" color="text.secondary" fontSize={11} mb={0.25}>
        {children}
    </Typography>
);

export const ColorDot = ({ color }: { color: string }) => (
    <Box
        sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: color,
            flexShrink: 0,
        }}
    />
);

// Operation helpers
export const getOperationIcon = (operation: BooleanOperation, size = 16) => {
    const sx = { width: size, height: size };
    switch (operation) {
        case BooleanOperation.AND:
            return <IntersectionBooleanIcon sx={sx} />;
        case BooleanOperation.OR:
            return <UniteBooleanIcon sx={sx} />;
        case BooleanOperation.AND_NOT:
            return <ExtractBooleanIcon sx={sx} />;
        case BooleanOperation.XOR:
            return <ExcludeBooleanIcon sx={sx} />;
    }
};

export const getOperationLabel = (operation: BooleanOperation) => {
    switch (operation) {
        case BooleanOperation.AND:
            return 'Intersection';
        case BooleanOperation.OR:
            return 'Unite';
        case BooleanOperation.AND_NOT:
            return 'Subtract';
        case BooleanOperation.XOR:
            return 'Exclude';
    }
};

// Structure option type
export interface StructureOption {
    value: string;
    roiNumber: number;
    label: string;
    color: string;
}

// Header component
interface HeaderProps {
    dragListeners: DraggableSyntheticListeners;
    onClose: () => void;
    closeTitle: string;
}

export function Header({ dragListeners, onClose, closeTitle }: HeaderProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 0.75,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                {...dragListeners}
                sx={{
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                    userSelect: 'none',
                    touchAction: 'none',
                }}
            >
                <GripVertical size={12} style={{ opacity: 0.6 }} />
                <Typography fontSize={14} fontWeight={500}>
                    Boolean Operation
                </Typography>
            </Stack>
            <IconButton
                size="small"
                onClick={onClose}
                sx={{ p: 0.25 }}
                title={closeTitle}
                aria-label={closeTitle}
            >
                <X size={16} />
            </IconButton>
        </Box>
    );
}

// Structure select component (single selection)
interface StructureSelectProps {
    label: string;
    value: string;
    onChange: (event: SelectChangeEvent) => void;
    options: StructureOption[];
    getColor: (name: string) => string;
    excludeRoiNumber?: number;
    placeholder?: string;
}

export function StructureSelect({
    label,
    value,
    onChange,
    options,
    getColor,
    excludeRoiNumber,
    placeholder = 'Select...',
}: StructureSelectProps) {
    const filteredOptions = excludeRoiNumber
        ? options.filter((o) => o.roiNumber !== excludeRoiNumber)
        : options;

    return (
        <Box>
            <Label>{label}</Label>
            <Select
                value={value}
                onChange={onChange}
                size="small"
                fullWidth
                sx={{ fontSize: 12 }}
                renderValue={(sel) => (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <ColorDot color={getColor(sel)} />
                        <Typography fontSize={12} noWrap>
                            {sel || placeholder}
                        </Typography>
                    </Stack>
                )}
                MenuProps={menuProps}
            >
                {filteredOptions.map((o) => (
                    <MenuItem
                        key={o.roiNumber}
                        value={o.value}
                        sx={{ fontSize: 12 }}
                    >
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <ColorDot color={o.color} />
                            {o.label}
                        </Stack>
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}

// Multi-select structure component
interface MultiStructureSelectProps {
    label: string;
    values: string[];
    onChange: (values: string[]) => void;
    options: StructureOption[];
    getColor: (name: string) => string;
    excludeRoiNumber?: number;
    placeholder?: string;
}

export function MultiStructureSelect({
    label,
    values,
    onChange,
    options,
    getColor,
    excludeRoiNumber,
    placeholder = 'Select structures...',
}: MultiStructureSelectProps) {
    const filteredOptions = excludeRoiNumber
        ? options.filter((o) => o.roiNumber !== excludeRoiNumber)
        : options;

    const handleChange = (event: SelectChangeEvent<string[]>) => {
        const val = event.target.value;
        onChange(typeof val === 'string' ? val.split(',') : val);
    };

    return (
        <Box>
            <Label>{label}</Label>
            <Select
                multiple
                value={values}
                onChange={handleChange}
                size="small"
                fullWidth
                sx={{ fontSize: 12 }}
                renderValue={(selected) => {
                    if (selected.length === 0) {
                        return (
                            <Typography fontSize={12} color="text.secondary">
                                {placeholder}
                            </Typography>
                        );
                    }
                    if (selected.length === 1) {
                        return (
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                            >
                                <ColorDot color={getColor(selected[0])} />
                                <Typography fontSize={12} noWrap>
                                    {selected[0]}
                                </Typography>
                            </Stack>
                        );
                    }
                    return (
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={0.5}
                            flexWrap="wrap"
                        >
                            {selected.slice(0, 2).map((name) => (
                                <Box
                                    key={name}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.25,
                                        bgcolor: 'action.selected',
                                        borderRadius: 0.5,
                                        px: 0.5,
                                        py: 0.125,
                                    }}
                                >
                                    <ColorDot color={getColor(name)} />
                                    <Typography fontSize={10} noWrap>
                                        {name.length > 8
                                            ? `${name.slice(0, 8)}...`
                                            : name}
                                    </Typography>
                                </Box>
                            ))}
                            {selected.length > 2 && (
                                <Typography
                                    fontSize={10}
                                    color="text.secondary"
                                >
                                    +{selected.length - 2}
                                </Typography>
                            )}
                        </Stack>
                    );
                }}
                MenuProps={menuProps}
            >
                {filteredOptions.map((o) => (
                    <MenuItem
                        key={o.roiNumber}
                        value={o.value}
                        sx={{ fontSize: 12 }}
                    >
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <Checkbox
                                checked={values.includes(o.value)}
                                size="small"
                                sx={{ p: 0.25 }}
                            />
                            <ColorDot color={o.color} />
                            {o.label}
                        </Stack>
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}

// Operation picker component
interface OperationPickerProps {
    value: BooleanOperation;
    onChange: (operation: BooleanOperation) => void;
    size?: 'normal' | 'small';
}

const operations = [
    BooleanOperation.OR,
    BooleanOperation.AND,
    BooleanOperation.AND_NOT,
    BooleanOperation.XOR,
];
export function OperationPicker({
    value,
    onChange,
    size = 'normal',
}: OperationPickerProps) {
    const intl = useIntl();
    const iconSize = size === 'small' ? 14 : 16;
    const py = size === 'small' ? 0.25 : 0.5;

    const handleChange = useCallback(
        (
            _event: React.MouseEvent<HTMLElement>,
            newValue: BooleanOperation | null
        ) => {
            if (newValue !== null) {
                onChange(newValue);
            }
        },
        [onChange]
    );

    return (
        <Box>
            {size === 'normal' && (
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Operation
                </Typography>
            )}

            <ToggleButtonGroup
                value={value}
                exclusive
                onChange={handleChange}
                size="small"
                fullWidth
                aria-label={intl.formatMessage({ id: 'selectOperation' })}
                sx={{
                    '& .MuiToggleButton-root': {
                        py,
                        px: 0.5,
                        gap: 1,
                        transition: 'all 0.35s ease-in-out',
                        textTransform: 'none',
                        '&:not(:first-of-type)': {
                            marginLeft: 0,
                        },
                        '&.Mui-selected': {
                            px: 2.5,
                        },
                    },
                }}
            >
                {operations.map((op) => {
                    const isSelected = op === value;
                    return (
                        <ToggleButton
                            key={op}
                            value={op}
                            aria-label={getOperationLabel(op)}
                        >
                            {getOperationIcon(op, iconSize)}
                            {isSelected && getOperationLabel(op)}
                        </ToggleButton>
                    );
                })}
            </ToggleButtonGroup>
        </Box>
    );
}
